import React, { useEffect, useState } from "react";
import { Download, FileText, Loader2, ShieldCheck, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "../api";
import { fmtDate, useLang } from "../i18n";
import { RiskBadge, VerifyBadge } from "../components/SubjectRegistry";

export default function Portal() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [att, setAtt] = useState({ filename: "", note: "", file_base64: null });
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/portal/me").then((r) => setData(r.data)).catch(() => setData({ linked: false }));
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return setAtt({ ...att, file_base64: null });
    if (f.size > 1_500_000) return toast.error(t("k.file_optional"));
    const rd = new FileReader();
    rd.onload = () => setAtt((p) => ({ ...p, file_base64: rd.result, filename: p.filename || f.name }));
    rd.readAsDataURL(f);
  };

  const upload = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      await api.post("/portal/attachments", att);
      toast.success(t("portal.uploaded"));
      setAtt({ filename: "", note: "", file_base64: null });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  if (!data) {
    return <div className="text-sm text-slate-500" data-testid="portal-loading"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("common.loading")}</div>;
  }

  const exp = data.expediente;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="portal-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-3">
          <ShieldCheck className="w-7 h-7 text-[#044CBC]" /> {t("portal.title")}
        </h1>
        <p className="text-sm text-slate-500 mt-1">{t("portal.subtitle")}</p>
      </div>

      {!data.linked || !exp ? (
        <div className="ec-card p-10 text-center text-sm text-slate-500" data-testid="portal-not-linked">{t("portal.not_linked")}</div>
      ) : (
        <>
          <div className="ec-card p-6 space-y-4" data-testid="portal-record">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="font-heading text-lg font-extrabold text-slate-900">{exp.name}</h2>
                {exp.client_id && <div className="text-xs font-mono text-[#033A8C]">{exp.client_id}</div>}
                <div className="text-xs text-slate-500 mt-1">{t("common.date")}: {fmtDate(exp.created_at)}</div>
              </div>
              <RiskBadge level={exp.risk?.level} t={t} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div><span className="ec-label">{t("portal.record")}</span><span className="text-xs font-semibold">{exp.id_number || "—"}</span></div>
              <div><span className="ec-label">{t("k.dd_level")}</span><span className="text-xs font-semibold">{t(`k.dd.${exp.risk?.dd_level}`)}</span></div>
              <div><span className="ec-label">{t("k.next_review")}</span><span className="text-xs">{exp.next_review || "—"}</span></div>
              <div>
                <span className="ec-label">{t("k.last_verify")}</span>
                <VerifyBadge verification={exp.verification} t={t} />
              </div>
            </div>
          </div>

          <div className="ec-card p-6" data-testid="portal-documents">
            <h3 className="font-heading font-bold text-slate-900 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#033A8C]" /> {t("portal.documents")}
            </h3>
            {(exp.attachments || []).length === 0 ? (
              <p className="text-sm text-slate-400">{t("portal.no_documents")}</p>
            ) : (
              <ul className="space-y-1.5">
                {exp.attachments.map((a) => (
                  <li key={a.id} className="flex items-center justify-between text-xs bg-slate-50 rounded px-3 py-2" data-testid={`portal-attachment-${a.id}`}>
                    <span className="font-semibold text-slate-700">{a.filename} <span className="font-normal text-slate-400">— {fmtDate(a.date)}</span></span>
                    {a.data !== undefined && (
                      <button onClick={() => downloadFile(`/portal/attachments/${a.id}/download`, a.filename)} className="text-[#044CBC] hover:text-[#033A8C]" data-testid={`portal-dl-${a.id}`}>
                        <Download className="w-4 h-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <form onSubmit={upload} className="mt-5 pt-4 border-t border-slate-100 space-y-3" data-testid="portal-upload-form">
              <p className="text-xs text-slate-500">{t("portal.upload_hint")}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input className="ec-input" placeholder={t("k.filename")} value={att.filename} onChange={(e) => setAtt({ ...att, filename: e.target.value })} required data-testid="portal-upload-filename" />
                <input className="ec-input" placeholder={t("portal.note")} value={att.note} onChange={(e) => setAtt({ ...att, note: e.target.value })} data-testid="portal-upload-note" />
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <input className="ec-input sm:max-w-xs" type="file" onChange={onFile} required data-testid="portal-upload-file" />
                <button type="submit" disabled={uploading || !att.file_base64} data-testid="portal-upload-submit" className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {t("portal.upload")}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
